"use client";

import { Button, Dialog, DialogActions, DialogContent, TextField, CircularProgress } from "@mui/material";
import Image from "next/image";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { useEffect, useState } from "react";
import { colorDisabledDisabled, colorPrimaryPrimary, textDisabledDisabled, textLinkOnSurfaceLink, textPrimaryOnColorPrimary } from "@/app/(styles)/variables";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { checkValidPincode } from "../../api";
import { pushGtmEvent } from "../../../../lib/utils/gtmEventHandler";
import questions from "./questions.json";
const steps = [{
  number: 1,
  label: "Tell us about your project"
}, {
  number: 2,
  label: "Browse contractors"
}, {
  number: 3,
  label: "Connect directly"
}];
const PincodePopup = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isPopupOpen = searchParams.get("isPopupOpen") === "true";
  const [showPincodeError, setShowPincodeError] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [pincode, setPincode] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("multiplyonboarding_pincode") || "";
    }
    return "";
  });
  const handlePincodeChange = e => {
    // console.log("pincode", pincode);
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setPincode(value);
    // Clear error when user starts typing or clears the input
    if (showPincodeError) {
      setShowPincodeError(false);
    }
  };
  const handleKeyDown = e_0 => {
    if (e_0.key === "Enter") {
      e_0.preventDefault();
      if (pincode.length === 6 && !isValidating) {
        handlePopupSubmit(e_0);
      }
    }
  };
  const handlePopupSubmit = async e_1 => {
    e_1.preventDefault();
    if (pincode.length !== 6) {
      return;
    }

    // Clear any previous error and show loading state
    setShowPincodeError(false);
    setIsValidating(true);
    try {
      const isValid = await checkValidPincode(pincode);
      if (!isValid) {
        setShowPincodeError(true);
        return;
      }
      setShowPincodeError(false);
      pushGtmEvent({
        event_name: "question_pincode_action_taken",
        event_action: "pincode_entered"
      });
      localStorage.setItem("multiplyonboarding_pincode", pincode);
      const params = new URLSearchParams(searchParams.toString());
      params.delete("isPopupOpen");
      router.replace(`/?${params.toString()}`, {
        scroll: false
      });
      const firstQuestionId = questions?.startScreen;
      setTimeout(() => {
        params.set("isQuestionSheetOpen", "true");
        params.set("questionId", firstQuestionId);
        router.replace(`${pathname}/?${params.toString()}`, {
          scroll: false
        });
      }, 200);
    } finally {
      setIsValidating(false);
    }
  };
  const handlePopupClose = () => {
    setShowPincodeError(false);
    setIsValidating(false);
    pushGtmEvent({
      event_name: "question_pincode_action_taken",
      event_action: "pincode_popup_closed"
    });
    const params = new URLSearchParams(searchParams.toString());
    params.delete("isPopupOpen");
    router.replace(`${pathname}/?${params.toString()}`, {
      scroll: false
    });
  };
  useEffect(() => {
    if (isPopupOpen) {
      pushGtmEvent({
        event_name: "question_pincode_viewed"
      });
    }
  }, [isPopupOpen]);
  let isFromCarpenterLandingPage = false;
  if (pathname.includes("your-carpenter")) {
    isFromCarpenterLandingPage = true;
  } else {
    isFromCarpenterLandingPage = false;
  }
  return <Dialog open={isPopupOpen} onClose={handlePopupClose} onFocus={e_2 => {
    setTimeout(() => {
      e_2.target.scrollIntoView({
        behavior: "smooth",
        block: "end"
      });
    }, 300);
  }} PaperProps={{
    sx: {
      width: "364px",
      height: "470px",
      maxWidth: "364px",
      borderRadius: "28px",
      overflow: "hidden",
      margin: 0,
      padding: 0,
      display: "flex"
    }
  }} slotProps={{
    backdrop: {
      sx: {
        backgroundColor: "rgba(0, 0, 0, 0.5)"
      }
    }
  }}>
      {/* Hero Image at top */}
      <div className="w-full h-[52px] relative shrink-0">
        <Image src="/images/Hero.avif" alt="Hero" fill style={{
        objectFit: "cover"
      }} priority />
      </div>

      <DialogContent sx={{
      padding: "24px 16px 0 16px",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      gap: "24px"
    }}>
        {/* Title */}
        <h2 className="m3-headline-small text-[--textPrimaryPriamry] text-center px-2">
          {isFromCarpenterLandingPage ? "Browse carpenters near you, in 3 steps" : "Browse contractors near you, in 3 steps"}
        </h2>

        {/* Steps */}
        <div className="flex justify-between items-start px-2">
          {steps.map(step => <div key={step.number} className="flex flex-col items-center w-[90px]">
              <div className="w-10 h-10 rounded-full bg-[#F1BE4B] flex items-center justify-center mb-2">
                <span className="m3-title-medium text-[--textPrimaryOnColorPrimary]">
                  {step.number}
                </span>
              </div>
              <p className="m3-label-medium text-[--textSecondarySecondary] text-center">
                {step.label}
              </p>
            </div>)}
        </div>

        {/* Pincode Input */}
        <div className="pt-6 px-4 space-y-2">
          <p className="m3-body-small text-[--textSecondarySecondary] ml-8">
            Enter your 6-digit area pin code
          </p>
          <div className="flex items-center gap-2">
            <LocationOnOutlinedIcon sx={{
            color: "#DEE1F9"
          }} />
            <TextField fullWidth error={showPincodeError} variant="standard" value={pincode} onChange={handlePincodeChange} onKeyDown={handleKeyDown} inputProps={{
            type: "tel",
            inputMode: "numeric",
            pattern: "[0-9]*"
          }}
          // autoFocus
          helperText={showPincodeError ? "Sorry, we don't serve this pincode yet." : ""}
          //   placeholder="Enter pincode"
          sx={{
            // "& .MuiInput-underline:before": {
            //   borderBottomColor: "#004BEE",
            //   borderBottomWidth: "3px",
            // },
            // "& .MuiInput-underline:after": {
            //   borderBottomColor: "#004BEE",
            //   borderBottomWidth: "3px",
            // },
            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
              borderBottomColor: "#004BEE",
              borderBottomWidth: "3px"
            }
          }} />
          </div>
        </div>
      </DialogContent>

      <DialogActions sx={{
      padding: "24px",
      justifyContent: "end",
      gap: "4px"
    }}>
        <Button onClick={handlePopupClose} sx={{
        color: textLinkOnSurfaceLink,
        textTransform: "none",
        backgroundColor: "transparent",
        minWidth: "80px",
        "&:hover": {
          backgroundColor: "transparent"
        }
      }} className="m3-label-large ">
          Cancel
        </Button>
        <Button variant={pincode.length === 6 ? "contained" : "outlined"} onClick={e_3 => handlePopupSubmit(e_3)} disabled={pincode.length !== 6 || isValidating} className="m3-label-large" sx={{
        backgroundColor: pincode.length === 6 ? colorPrimaryPrimary : "transparent",
        borderRadius: "24px",
        padding: "6px 20px",
        // decreased from 10px 32px
        minWidth: "80px",
        // decreased from 100px
        color: pincode.length === 6 ? textPrimaryOnColorPrimary : textDisabledDisabled,
        borderColor: pincode.length !== 6 ? colorDisabledDisabled : colorPrimaryPrimary,
        "&:hover": {
          backgroundColor: pincode.length === 6 ? colorPrimaryPrimary : "transparent",
          borderColor: pincode.length !== 6 ? colorDisabledDisabled : colorPrimaryPrimary
        },
        "&.Mui-disabled": {
          backgroundColor: "transparent",
          color: textDisabledDisabled,
          borderColor: colorDisabledDisabled
        }
      }}>
          {isValidating ? <CircularProgress size={20} sx={{
          color: textPrimaryOnColorPrimary
        }} /> : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>;
};
export default PincodePopup;